import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

/** Presupuestos sin archivos (evita reintentos). */
const presupuestosSinArchivos = new Set();
/** Peticiones en curso por numeroPresupuesto (deduplica llamadas simultáneas). */
const inflightByPresupuesto = new Map();

const extractNumeroPresupuestoFromPath = (filePath) => {
  if (!filePath) return null;
  const normalized = String(filePath).replace(/\\/g, "/");
  const match = normalized.match(/\/Proyectos\/([^/]+)\//i);
  return match?.[1]?.trim() || null;
};

/** Limpia la caché negativa tras subir/borrar archivos. */
export const invalidatePresupuestoFilesCache = (numeroPresupuesto) => {
  const key = String(numeroPresupuesto || "").trim();
  if (!key) return;
  presupuestosSinArchivos.delete(key);
  inflightByPresupuesto.delete(key);
};
const MAX_CONCURRENT_FILE_REQUESTS = 6;
let activeFileRequests = 0;
const fileRequestWaiters = [];

const acquireFileRequestSlot = () =>
  new Promise((resolve) => {
    const grant = () => {
      activeFileRequests += 1;
      resolve();
    };
    if (activeFileRequests < MAX_CONCURRENT_FILE_REQUESTS) {
      grant();
      return;
    }
    fileRequestWaiters.push(grant);
  });

const releaseFileRequestSlot = () => {
  activeFileRequests = Math.max(0, activeFileRequests - 1);
  const next = fileRequestWaiters.shift();
  if (next) next();
};

const axiosGetFiles = (url, params, options = {}) =>
  axios.get(url, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    params,
    signal: options.signal,
    validateStatus: (status) => status === 200 || status === 404,
  });

export const uploadProjectFile = async (file, IdPresupuesto) => {
  if (!file || !IdPresupuesto) {
    throw new Error("Se requiere un archivo y un IdPresupuesto.");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("IdPresupuesto", String(IdPresupuesto));

  try {
    const token = localStorage.getItem("token");
    const response = await axios.post(
      `${API_URL}/api/projectFiles/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );

    const data = response.data;
    const numeroPresupuesto = extractNumeroPresupuestoFromPath(data?.filePath);
    if (numeroPresupuesto) {
      invalidatePresupuestoFilesCache(numeroPresupuesto);
    }

    return data;
  } catch (error) {
    console.error("Error al subir el archivo:", error);
    throw error;
  }
};

const fetchFileContentOnce = async (numeroPresupuesto, options = {}) => {
  const key = String(numeroPresupuesto);

  if (presupuestosSinArchivos.has(key)) {
    return [];
  }

  await acquireFileRequestSlot();
  try {
    const response = await axiosGetFiles(
      `${process.env.REACT_APP_API_URL}/api/projectFiles/readfile`,
      { numeroPresupuesto: key },
      options,
    );

    if (response.status === 200 && Array.isArray(response.data)) {
      return response.data;
    }

    if (response.status === 404) {
      const responseFlex = await axiosGetFiles(
        `${process.env.REACT_APP_API_URL}/api/projectFiles/readfileFlex`,
        { numeroPresupuesto: key },
        options,
      );
      if (responseFlex.status === 200 && Array.isArray(responseFlex.data)) {
        return responseFlex.data;
      }
      presupuestosSinArchivos.add(key);
      return [];
    }

    presupuestosSinArchivos.add(key);
    return [];
  } finally {
    releaseFileRequestSlot();
  }
};

export const getFileContent = async (numeroPresupuesto, options = {}) => {
  const token = localStorage.getItem("token");

  if (!token) {
    return [];
  }

  const key = String(numeroPresupuesto);
  if (!key) return [];

  if (presupuestosSinArchivos.has(key)) {
    return [];
  }

  if (inflightByPresupuesto.has(key)) {
    return inflightByPresupuesto.get(key);
  }

  const promise = fetchFileContentOnce(key, options)
    .catch(() => {
      presupuestosSinArchivos.add(key);
      return [];
    })
    .finally(() => {
      inflightByPresupuesto.delete(key);
    });

  inflightByPresupuesto.set(key, promise);
  return promise;
};

export const deleteFile = async (filePath) => {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Error("Token no disponible para la eliminación.");
  }

  try {
    const response = await axios.delete(
      `${API_URL}/api/projectFiles/deletefile`,

      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { filePath },
      },
    );
    const data = response.data;
    invalidatePresupuestoFilesCache(extractNumeroPresupuestoFromPath(filePath));
    return data;
  } catch (error) {
    console.error("Error al eliminar archivo:", error);
    throw error;
  }
};

// pillar fotos dpara Muestras en CriticalPathHorizontal1
export const getMuestraFiles = async (idMuestra) => {
  const token = localStorage.getItem("token");

  const res = await fetch(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${idMuestra}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!res.ok) return [];

  return res.json();
};

// pillar fotos dpara Muestras en CriticalPathHorizontal1
export const getMuestraFileContent = async (path) => {
  const token = localStorage.getItem("token");

  const response = await axios.get(`${API_URL}/api/files/readfile`, {
    params: { path },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return response.data;
};
